import { useQuery } from '@tanstack/react-query'
import { getShopItemStats } from '@/requests/analytics'
import { fetchItemsByShop } from '@/features/items/data/requests'
import dayjs from 'dayjs'
import { useShop } from '@/context/shop-context'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Info, TrendingUp } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { useTranslation } from '@/hooks/use-translation'
import { useState } from 'react'

interface Stat {
  value: string
  total: number
  popularity_score?: number
  trend_score?: number
  is_trending?: boolean
}

interface AnalyticsResponse {
  items: Stat[]
  total: number
  period: {
    start: number
    end: number
  }
}

export function PopularItemsAnalyticsCard() {
  const { activeShop } = useShop()
  const { t } = useTranslation()
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const { data, isLoading } = useQuery<AnalyticsResponse>({
    queryKey: ['analytics.shop.menu.stat', activeShop?._id],
    queryFn: () =>
      getShopItemStats(
        activeShop?._id || '',
        dayjs().startOf('day').valueOf(),
        dayjs().endOf('day').valueOf()
      ),
    enabled: !!activeShop?._id,
  })

  const { data: items, isLoading: isItemsLoading } = useQuery({
    queryKey: ['menu.list', activeShop?._id],
    queryFn: fetchItemsByShop(),
    enabled: !!activeShop?._id,
  })

  // Extract analytics items from response (handle both old and new format)
  const analyticsItems = data?.items || (Array.isArray(data) ? data : [])

  const getItemName = (itemId: string) => {
    const item = items?.find((item: any) => item._id === itemId)
    if (!item || !item.name) return ''
    const lang = activeShop?.ui_language || 'en'
    const translatedName = item.name[lang]
    // If translation exists and is not empty, return it
    if (translatedName && translatedName.trim() !== '') {
      return translatedName
    }
    // Fallback to first available value in name array
    const nameValues = Object.values(item.name) as string[]
    return nameValues.find((name) => name && name.trim() !== '') || ''
  }

  return (
    <Card className='popular-items-section col-span-1 lg:col-span-3'>
      <CardHeader className='flex flex-row items-start justify-between'>
        <div>
          <CardTitle>{t('Popular Items')}</CardTitle>
        </div>
        <TooltipProvider delayDuration={0}>
          <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild>
              <Button 
                variant='ghost' 
                size='icon' 
                className='h-8 w-8 p-0'
                onClick={() => setTooltipOpen(!tooltipOpen)}
              >
                <Info className='h-4 w-4 text-muted-foreground' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='top'>
              {t('Menu items with the most clicks today.')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent>
        <div className='space-y-8'>
          {isLoading || isItemsLoading ? (
            <>
              <div className='flex items-center gap-4'>
                <Skeleton className='h-4 w-[20px]' />
                <div className='flex flex-1 flex-wrap items-center justify-between'>
                  <div className='space-y-1'>
                    <div className='text-sm font-medium leading-none'>
                      <Skeleton className='h-4 w-[150px]' />
                    </div>
                  </div>
                  <div className='font-medium'>
                    <Skeleton className='h-4 w-[20px]' />
                  </div>
                </div>
              </div>
            </>
          ) : analyticsItems && analyticsItems.length > 0 ? (
            <>
              {analyticsItems.map((item: Stat, index: number) => (
                <div className='flex items-center gap-4' key={item.value}>
                  <Badge>{index + 1}</Badge>
                  <div className='flex flex-1 flex-wrap items-center justify-between'>
                    <div className='space-y-1 flex items-center gap-2'>
                      <p className='text-sm font-medium leading-none'>
                        {getItemName(item.value)}
                      </p>
                      {item.is_trending && (
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <TrendingUp 
                                className='h-4 w-4' 
                                style={{ color: '#fbbf24' }} 
                                fill='#fbbf24'
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Trending: {item.trend_score?.toFixed(1)}% growth</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className='font-medium'>{item.total}</div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className='flex h-[350px] flex-col items-center justify-center'>
              <img
                src='/images/items-no-data.svg'
                alt='No item analytics data'
                style={{ maxHeight: 180, marginBottom: 12 }}
              />
              <span
                style={{
                  marginTop: 8,
                  color: '#555',
                  fontSize: 17,
                  fontWeight: 500,
                  textAlign: 'center',
                  maxWidth: 320,
                  lineHeight: 1.5,
                }}
              >
                No item data yet. Top picks will show up here!
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
